import { useState } from "react";
import { Leaf, Coffee, Droplet, Circle, FlaskConical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import lionsManeImage from "@/assets/lions-mane-mushroom.jpg";
import schisandraImage from "@/assets/schisandra-berries.jpg";
import greenTeaImage from "@/assets/green-tea-leaves.jpg";
import lTheanineImage from "@/assets/l-theanine-tea.jpg";

interface Ingredient {
  name: string;
  dosage: string;
  source: string;
  benefits: string[];
  image: string;
  icon: JSX.Element;
}

const ingredients: Ingredient[] = [
  {
    name: "Lion's Mane Extract",
    dosage: "500mg",
    source: "Sourced from organic fruiting bodies, 10:1 concentration",
    benefits: [
      "Stimulates Nerve Growth Factor (NGF) production for brain cell regeneration",
      "Clinically shown to improve memory and reduce brain fog",
      "Used in traditional Japanese medicine for centuries",
    ],
    image: lionsManeImage,
    icon: <Circle className="text-amber-500" />,
  },
  {
    name: "L-Theanine",
    dosage: "200mg",
    source: "Derived from premium green tea leaves",
    benefits: [
      "Promotes calm, focused alertness without drowsiness",
      "Synergises with caffeine to smooth out energy spikes and crashes",
      "Increases alpha brain wave activity associated with relaxed focus",
    ],
    image: lTheanineImage,
    icon: <Leaf className="text-emerald-500" />,
  },
  {
    name: "Schisandra Berry Extract",
    dosage: "250mg",
    source: "Wild-harvested, 10:1 concentration",
    benefits: [
      "Powerful adaptogen that helps the body resist physical and mental stress",
      "Shown to improve endurance and mental performance under pressure",
      "Supports liver health and overall vitality",
    ],
    image: schisandraImage,
    icon: <Droplet className="text-red-500" />,
  },
  {
    name: "Green Tea Extract",
    dosage: "50mg natural caffeine",
    source: "Providing clean, natural caffeine from whole leaf extract",
    benefits: [
      "Clean, gentle energy without the jolt of synthetic caffeine",
      "Rich in antioxidants (EGCG) that protect brain cells",
      "Sustained release compared to coffee — no crash",
    ],
    image: greenTeaImage,
    icon: <Coffee className="text-green-500" />,
  },
];

const IngredientsSection = () => {
  const [activeIngredient, setActiveIngredient] = useState(ingredients[0]);

  return (
    <section className="py-20">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <h2 className="text-3xl md:text-4xl font-bold">
              Premium <span className="neural-text">Ingredients</span>
            </h2>
            <Badge className="bg-solr-nebula-purple/20 text-solr-nebula-purple border-solr-nebula-purple/30 hover:bg-solr-nebula-purple/30 gap-1.5">
              <FlaskConical className="h-3 w-3" />
              Clinically Studied
            </Badge>
          </div>
          <p className="text-solr-starfield-silver">
            Each ingredient is carefully selected at precise dosages backed by clinical research,
            creating the perfect synergistic blend for cognitive enhancement.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Ingredient selector */}
          <div className="col-span-1 order-2 lg:order-1">
            <div className="flex flex-col space-y-2">
              {ingredients.map((ingredient) => (
                <button
                  key={ingredient.name}
                  onClick={() => setActiveIngredient(ingredient)}
                  className={`p-4 text-left rounded-lg transition-all ${
                    activeIngredient.name === ingredient.name
                      ? "bg-solr-nebula-purple/20 border border-solr-nebula-purple/30"
                      : "hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-center">
                    <div className="mr-4">{ingredient.icon}</div>
                    <div>
                      <h4 className="font-medium">
                        {ingredient.name}{" "}
                        <span className="text-solr-electric-blue font-semibold">
                          ({ingredient.dosage})
                        </span>
                      </h4>
                      <span className="text-xs text-solr-starfield-silver/70">
                        {ingredient.source}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail panel */}
          <div className="col-span-2 order-1 lg:order-2">
            <div className="cosmic-card h-full p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    {activeIngredient.icon}
                    <Badge variant="outline" className="text-solr-nebula-purple border-solr-nebula-purple/30 text-xs">
                      Science-Backed
                    </Badge>
                  </div>
                  <h3 className="text-2xl font-bold mt-2">
                    {activeIngredient.name}{" "}
                    <span className="text-solr-electric-blue">({activeIngredient.dosage})</span>
                  </h3>
                  <p className="text-sm text-solr-starfield-silver/70 mt-1 mb-6">
                    {activeIngredient.source}
                  </p>

                  <h4 className="text-sm font-medium mb-3">Proven Cognitive Benefits:</h4>
                  <ul className="space-y-3">
                    {activeIngredient.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <div className="bg-solr-nebula-purple/20 rounded-full p-1 mr-3 mt-0.5 shrink-0">
                          <div className="w-2 h-2 rounded-full bg-solr-nebula-purple"></div>
                        </div>
                        <span className="text-sm text-solr-starfield-silver">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex justify-center items-start">
                  <div className="relative w-full">
                    <div className="absolute inset-0 bg-gradient-to-br from-solr-nebula-purple/20 to-solr-electric-blue/10 rounded-lg filter blur-xl opacity-70"></div>
                    <img
                      src={activeIngredient.image}
                      alt={activeIngredient.name}
                      className="relative z-10 rounded-lg w-full h-[300px] object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IngredientsSection;
